import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import stockTransferService from "../services/stockTransferService";
import StorageService from "../services/StorageService";
import ProjectItemService from "../services/ProjectItemService";
import ProjectStoreService from "../services/ProjectStoreService";
import MessageDialog from "../components/MessageDialog";
import utility from "../utils/utility";

export default function ReceiveStockTransfer() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [header, setHeader] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unassignedItems, setUnassignedItems] = useState([]);
  const [scrapStores, setScrapStores] = useState([]);

  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return utility.formatDateDDMMMYYYY(dateStr);
  };

  const formatEmployee = (name, code) => {
    if (name && code) return `${name} (${code})`;
    return name || code || "-";
  };

  const formatProject = (name, code, categoryName) => {
    const categoryInitial = String(categoryName || "").trim().charAt(0).toUpperCase();
    if (name) {
      if (categoryInitial) return `${categoryInitial}*${name}`;
      if (code) {
        const codeInitial = String(code).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${name}`;
      }
      return name;
    }
    return "-";
  };

  const formatQty3 = (val) => {
    if (val === null || val === undefined || val === "") return "0.000";
    return Number(val).toFixed(3);
  };

  useEffect(() => {

    const loadDetails = async () => {

      try {

        setLoading(true);

        const res = await stockTransferService.getById(id);

        const data = res?.data;

        setHeader(data);

        const items = (data?.stockTransferDetails || []).map((r) => ({
          ...r,
          receivedQuantity: formatQty3(r.quantity),
          damagedQuantity: "0.000",
          scrapStoreId: "",
          lostQuantity: "0.000",
          remarks: "",
        }));

        setRows(items);

        if (data?.toProjectId) {
          const projectItemsRes = await ProjectItemService.getByProjectId(data.toProjectId);
          const projectItems = projectItemsRes?.data?.data || [];
          
          const activeProjectItemIds = new Set(
            projectItems
              .filter(pi => pi.status === "Active")
              .map(pi => pi.itemId)
          );

          const unassigned = (data?.stockTransferDetails || []).filter(
            item => !activeProjectItemIds.has(item.itemId)
          );
          setUnassignedItems(unassigned);

          // Fetch active project stores to find Scrap Stores
          const storeRes = await ProjectStoreService.getProjectStoreById(data.toProjectId);
          const projectStoresList = storeRes?.data || storeRes || [];
          const scrapList = (Array.isArray(projectStoresList) ? projectStoresList : []).filter(s => {
            const isStoreActive = s.status === "Active" && (s.storeStatus === undefined || s.storeStatus === "Active");
            const code = String(s.storeCode || "").toUpperCase();
            const name = String(s.storeName || "").toUpperCase();
            return isStoreActive && (code.includes("SCRAP") || name.includes("SCRAP"));
          });
          setScrapStores(scrapList);
        }

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);

      }

    };

    if (id) loadDetails();

  }, [id]);

  const handleChange = (index, field, value) => {
    const updated = [...rows];

    if (field === "remarks" || field === "scrapStoreId") {
      updated[index][field] = value;
    } else {
      // allow only numbers + decimal with at most 3 decimal places
      if (value !== "" && !/^\d*\.?\d{0,3}$/.test(value)) return;

      if (field === "damagedQuantity" && parseFloat(value) > 0) {
        if (scrapStores.length === 0) {
          setMessage(`There is no active Scrap Store assigned to destination project (${header?.toProjectName || ""}). Cannot enter damaged quantity.`);
          setMsgType("error");
          updated[index][field] = "0.000";
          updated[index]["scrapStoreId"] = "";
          setRows(updated);
          return;
        }
        if (scrapStores.length === 1 && !updated[index]["scrapStoreId"]) {
          updated[index]["scrapStoreId"] = String(scrapStores[0].storeId || scrapStores[0].id);
        }
      } else if (field === "damagedQuantity" && (value === "" || parseFloat(value) === 0)) {
        updated[index]["scrapStoreId"] = "";
      }

      updated[index][field] = value;
    }

    setRows(updated);
  };

  const handleBlur = (index, field) => {
    const updated = [...rows];
    const val = updated[index][field];

    if (field !== "remarks" && field !== "scrapStoreId") {
      if (val !== "" && !isNaN(Number(val))) {
        const numVal = Number(val);
        updated[index][field] = numVal.toFixed(3);
        if (field === "damagedQuantity") {
          if (numVal > 0) {
            if (scrapStores.length === 0) {
              setMessage(`There is no active Scrap Store assigned to destination project (${header?.toProjectName || ""}). Cannot enter damaged quantity.`);
              setMsgType("error");
              updated[index]["damagedQuantity"] = "0.000";
              updated[index]["scrapStoreId"] = "";
            } else if (scrapStores.length === 1 && !updated[index]["scrapStoreId"]) {
              updated[index]["scrapStoreId"] = String(scrapStores[0].storeId || scrapStores[0].id);
            }
          } else {
            updated[index]["scrapStoreId"] = "";
          }
        }
      } else {
        updated[index][field] = "0.000";
        if (field === "damagedQuantity") {
          updated[index]["scrapStoreId"] = "";
        }
      }
    }

    setRows(updated);
  };

  const validateRows = () => {

    for (const row of rows) {

      const total =
        parseFloat(row.receivedQuantity || 0) +
        parseFloat(row.damagedQuantity || 0) +
        parseFloat(row.lostQuantity || 0);

      if (Number(total.toFixed(3)) !== Number(parseFloat(row.quantity).toFixed(3))) {

        setMessage(`Total quantities must equal Transfer Qty for item ${row.itemCode}`);
        setMsgType("error");

        return false;

      }

      if (parseFloat(row.damagedQuantity || 0) > 0) {
        if (scrapStores.length === 0) {
          setMessage(`There is no active Scrap Store assigned to destination project (${header?.toProjectName || ""}). Cannot receive damaged items.`);
          setMsgType("error");
          return false;
        }

        if (!row.scrapStoreId) {
          setMessage(`Please select a Scrap Store for item ${row.itemCode} where damaged quantity is entered.`);
          setMsgType("error");
          return false;
        }
      }

    }

    return true;

  };

  const handleSave = async () => {

    if (!validateRows()) return;

    try {

      setSaving(true);

      const payload = {
        employeeId: StorageService.loginData().employeeId,
        details: rows.map((r) => ({
          detailId: r.detailId,
          itemId: r.itemId,
          UOM: r.UOM,
          receivedQuantity: parseFloat(r.receivedQuantity || 0),
          damagedQuantity: parseFloat(r.damagedQuantity || 0),
          lostQuantity: parseFloat(r.lostQuantity || 0),
          scrapStoreId: parseFloat(r.damagedQuantity || 0) > 0 ? Number(r.scrapStoreId) : null,
          remarks: r.remarks || "",
        })),
      };

      await stockTransferService.receiveTransfer(id, payload);

      setMessage("Stock received successfully");
      setMsgType("success");

      navigate("/cpnl/Stocktransfer/receive");

    } catch (err) {

      console.error(err);

      setMessage(err.response?.data?.message || "Receive failed");
      setMsgType("error");

    } finally {

      setSaving(false);

    }

  };

  return (
    <>
      <Breadcrumb
        pageTitle="Receive Stock Transfer"
        secondTitle="Inventory"
      />
      
        <MessageDialog
          message={message}
          type={msgType}
          setMessage={setMessage}
          setMsgType={setMsgType}
        />
      

      <div className="card p-3 bg-white shadow-sm">

        {unassignedItems.length > 0 && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>Warning!</strong> The following items in this transfer are not assigned to the destination project:{" "}
            <strong>
              {unassignedItems.map(item => `${item.itemCode} (${item.itemDescription || ""})`).join(", ")}
            </strong>
            . Please assign them to the destination project with 'Active' status before receiving this transfer.
          </div>
        )}

        {header && (
          <div className="row mb-3">

            <div className="col-md-4">
              <strong>Voucher No:</strong> {header.vocherNo}
            </div>

            <div className="col-md-4 text-end">
              <strong>Transfer Date:</strong>{" "}
              {formatDate(header.transferDate)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>From Company:</strong> {header.fromCompanyName}
            </div>

            <div className="col-md-6 mt-2">
              <strong>To Company:</strong> {header.toCompanyName}
            </div>

            <div className="col-md-6 mt-2">
              <strong>From Project:</strong> {formatProject(header.fromProjectName, header.fromProjectCode, header.fromProjectCategoryName)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>To Project:</strong> {formatProject(header.toProjectName, header.toProjectCode, header.toProjectCategoryName)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>From Store:</strong> {header.fromStoreName}
            </div>

            <div className="col-md-6 mt-2">
              <strong>To Store:</strong> {header.toStoreName}
            </div>

            <div className="col-md-12 mt-2">
              <strong>Remarks / Transfer Note:</strong> {header.stockTransferNote || "-"}
            </div>

            <div className="col-md-6 mt-2">
              <strong>Created By:</strong> {formatEmployee(header.createdByName, header.createdByCode)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>Received / Approved By:</strong> {formatEmployee(header.receivedByName, header.receivedByCode)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>Rejected By:</strong> {formatEmployee(header.rejectedByName, header.rejectedByCode)}
            </div>

            <div className="col-md-6 mt-2">
              <strong>Cancelled By:</strong> {formatEmployee(header.cancelledByName, header.cancelledByCode)}
            </div>

            {header.status === "rejected" && (
              <div className="col-md-12 mt-2">
                <strong>Rejection Notes:</strong> {header.rejectedNotes || "-"}
              </div>
            )}

          </div>
        )}

        <div className="table-responsive">

          <table className="table table-bordered table-striped">

            <thead>
              <tr>
                <th>Item</th>
                <th>UOM</th>
                <th>Transfer Qty</th>
                <th>Received Qty</th>
                <th>Damaged Qty</th>
                <th>Scrap Store</th>
                <th>Lost Qty</th>
                <th>Remarks</th>
              </tr>
            </thead>

            <tbody>

              {loading ? (

                <tr>
                  <td colSpan="8" className="text-center">
                    Loading...
                  </td>
                </tr>

              ) : rows.length > 0 ? (

                rows.map((row, idx) => (

                  <tr key={idx}>

                    <td>{row.itemCode}</td>

                    <td>{row.UOM}</td>

                    <td>{row.quantity}</td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={row.receivedQuantity}
                        onChange={(e) =>
                          handleChange(idx, "receivedQuantity", e.target.value)
                        }
                        onBlur={() => handleBlur(idx, "receivedQuantity")}
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={row.damagedQuantity}
                        onChange={(e) =>
                          handleChange(idx, "damagedQuantity", e.target.value)
                        }
                        onBlur={() => handleBlur(idx, "damagedQuantity")}
                      />
                    </td>

                    <td>
                      {parseFloat(row.damagedQuantity || 0) > 0 ? (
                        <select
                          className={`form-select ${!row.scrapStoreId ? "is-invalid" : ""}`}
                          value={row.scrapStoreId || ""}
                          onChange={(e) =>
                            handleChange(idx, "scrapStoreId", e.target.value)
                          }
                        >
                          <option value="">Select Scrap Store</option>
                          {scrapStores.map((s) => (
                            <option key={s.storeId || s.id} value={s.storeId || s.id}>
                              {s.storeName} ({s.storeCode})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={row.lostQuantity}
                        onChange={(e) =>
                          handleChange(idx, "lostQuantity", e.target.value)
                        }
                        onBlur={() => handleBlur(idx, "lostQuantity")}
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={row.remarks || ""}
                        onChange={(e) =>
                          handleChange(idx, "remarks", e.target.value)
                        }
                        placeholder="Enter remarks"
                      />
                    </td>

                  </tr>

                ))

              ) : (

                <tr>
                  <td colSpan="8" className="text-center">
                    No items found
                  </td>
                </tr>

              )}

            </tbody>

          </table>

        </div>

        <div className="mt-3">

          <button
            className="btn btn-success me-2"
            onClick={handleSave}
            disabled={saving || unassignedItems.length > 0}
          >
            {saving ? "Saving..." : "Receive Stock"}
          </button>

          <button
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>

        </div>

      </div>
    </>
  );
}